/**
 * Graphic objects GSPS ↔ mediciones Cornerstone Tools v4.
 */

function handle(x, y, extra) {
  return { x, y, highlight: true, active: false, ...(extra || {}) }
}

function textBox() {
  return {
    active: false,
    hasMoved: false,
    movesIndependently: false,
    drawnIndependently: true,
    allowedOutsideImage: true,
    hasBoundingBox: true,
  }
}

function polylinePoints(g) {
  return (g && g.points) || []
}

function looksClosedRect(pts) {
  if (!pts || pts.length < 5) return false
  const a = pts[0]
  const z = pts[pts.length - 1]
  if (Math.abs(a.x - z.x) > 0.5 || Math.abs(a.y - z.y) > 0.5) return false
  const unique = pts.slice(0, 4)
  if (unique.length < 4) return false
  const xs = unique.map((p) => p.x)
  const ys = unique.map((p) => p.y)
  const minX = Math.min.apply(null, xs)
  const maxX = Math.max.apply(null, xs)
  const minY = Math.min.apply(null, ys)
  const maxY = Math.max.apply(null, ys)
  return unique.every(
    (p) =>
      (Math.abs(p.x - minX) < 0.5 || Math.abs(p.x - maxX) < 0.5) &&
      (Math.abs(p.y - minY) < 0.5 || Math.abs(p.y - maxY) < 0.5)
  )
}

export function graphicToMeasurement(graphic) {
  if (!graphic) return null
  const type = String(graphic.graphicType || '').toUpperCase()
  const pts = polylinePoints(graphic)
  if (type === 'TEXT') return null

  if (type === 'POLYLINE' || type === 'INTERPOLATED') {
    if (pts.length === 2) {
      const [a, b] = pts
      return {
        tool: 'Length',
        note: '',
        data: {
          visible: true,
          active: true,
          handles: { start: handle(a.x, a.y), end: handle(b.x, b.y), textBox: textBox() },
          unit: 'mm',
        },
      }
    }
    if (pts.length === 3) {
      const [a, m, b] = pts
      return {
        tool: 'Angle',
        note: '',
        data: {
          visible: true,
          active: true,
          handles: {
            start: handle(a.x, a.y),
            middle: handle(m.x, m.y),
            end: handle(b.x, b.y),
            textBox: textBox(),
          },
        },
      }
    }
    if (pts.length >= 5 && looksClosedRect(pts)) {
      const xs = pts.map((p) => p.x)
      const ys = pts.map((p) => p.y)
      return {
        tool: 'RectangleRoi',
        note: '',
        data: {
          visible: true,
          active: true,
          handles: {
            start: handle(Math.min.apply(null, xs), Math.min.apply(null, ys)),
            end: handle(Math.max.apply(null, xs), Math.max.apply(null, ys)),
            textBox: textBox(),
          },
        },
      }
    }
    if (pts.length >= 4) {
      const [a, b, c, d] = pts
      return {
        tool: 'CobbAngle',
        note: '',
        data: {
          visible: true,
          active: true,
          handles: {
            start: handle(a.x, a.y),
            end: handle(b.x, b.y),
            start2: handle(c.x, c.y),
            end2: handle(d.x, d.y),
            textBox: textBox(),
          },
        },
      }
    }
  }

  if (type === 'ELLIPSE' && pts.length >= 4) {
    const xs = pts.map((p) => p.x)
    const ys = pts.map((p) => p.y)
    return {
      tool: 'EllipticalRoi',
      note: '',
      data: {
        visible: true,
        active: true,
        handles: {
          start: handle(Math.min.apply(null, xs), Math.min.apply(null, ys)),
          end: handle(Math.max.apply(null, xs), Math.max.apply(null, ys)),
          textBox: textBox(),
        },
      },
    }
  }

  if (type === 'CIRCLE' && pts.length >= 2) {
    const [c, r] = pts
    const rad = Math.hypot(r.x - c.x, r.y - c.y)
    return {
      tool: 'EllipticalRoi',
      note: '',
      data: {
        visible: true,
        active: true,
        handles: {
          start: handle(c.x - rad, c.y - rad),
          end: handle(c.x + rad, c.y + rad),
          textBox: textBox(),
        },
      },
    }
  }

  return null
}

export function gspsToMeasurements(parsed) {
  if (!parsed || !Array.isArray(parsed.graphics)) return []
  return parsed.graphics.map(graphicToMeasurement).filter(Boolean)
}

function ptsFromHandles(handles, keys) {
  const out = []
  keys.forEach((k) => {
    const h = handles && handles[k]
    if (h && h.x != null && h.y != null) out.push(h.x, h.y)
  })
  return out
}

/**
 * Medición Cornerstone → Graphic Object GSPS.
 */
export function measurementToGraphic(measure) {
  if (!measure || !measure.data) return null
  const h = measure.data.handles || {}
  const tool = measure.tool
  if (tool === 'Length') {
    const data = ptsFromHandles(h, ['start', 'end'])
    if (data.length < 4) return null
    return { graphicType: 'POLYLINE', graphicData: data, label: measure.note || 'Length' }
  }
  if (tool === 'Angle') {
    const data = ptsFromHandles(h, ['start', 'middle', 'end'])
    if (data.length < 6) return null
    return { graphicType: 'POLYLINE', graphicData: data, label: measure.note || 'Angle' }
  }
  if (tool === 'CobbAngle') {
    const data = ptsFromHandles(h, ['start', 'end', 'start2', 'end2'])
    if (data.length < 8) return null
    return { graphicType: 'POLYLINE', graphicData: data, label: measure.note || 'Cobb' }
  }
  if (tool === 'EllipticalRoi' || tool === 'RectangleRoi') {
    const data = ptsFromHandles(h, ['start', 'end'])
    if (data.length < 4) return null
    const x0 = data[0]
    const y0 = data[1]
    const x1 = data[2]
    const y1 = data[3]
    if (tool === 'RectangleRoi') {
      return {
        graphicType: 'POLYLINE',
        graphicData: [x0, y0, x1, y0, x1, y1, x0, y1, x0, y0],
        label: measure.note || 'RectangleRoi',
      }
    }
    return {
      graphicType: 'ELLIPSE',
      graphicData: [x0, (y0 + y1) / 2, x1, (y0 + y1) / 2, (x0 + x1) / 2, y0, (x0 + x1) / 2, y1],
      label: measure.note || 'EllipticalRoi',
    }
  }
  return null
}

export function measurementsToGraphics(measurements) {
  return (measurements || []).map(measurementToGraphic).filter(Boolean)
}
